import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { getProduct, getProducts } from '../firebase/services';
import { applyPriceMarkup } from '../utils/priceUtils';
import ProductCard from '../components/ProductCard';
import SEOHelmet from '../components/SEOHelmet';
import { generateProductSchema, generateBreadcrumbSchema } from '../utils/seo';
import '../styles/ProductDetail.css';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [product, setProduct] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [weightKg, setWeightKg] = useState('1'); // Default 1 kg
  const [weightGram, setWeightGram] = useState('0'); // Additional grams
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadProduct();
  }, [id]);

  const loadProduct = async () => {
    try {
      const productData = await getProduct(id);
      if (productData) {
        setProduct(productData);
        if (productData.variants && productData.variants.length > 0) {
          setSelectedVariant(productData.variants[0]);
        }
        
        // Load related products
        const allProducts = await getProducts();
        const related = allProducts.filter(p => p.id !== id).slice(0, 4);
        setRelatedProducts(related);
      }
    } catch (error) {
      console.error('Error loading product:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="product-detail">
        <div className="loading">Loading product...</div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="product-detail">
        <div className="not-found">
          <h2>Product not found</h2>
          <button onClick={() => navigate('/shop')} className="btn-back">
            Back to Shop
          </button>
        </div>
      </div>
    );
  }

  // Calculate total weight in kg
  const getTotalWeightInKg = () => {
    const kg = parseFloat(weightKg) || 0;
    const gram = parseFloat(weightGram) || 0;
    return kg + (gram / 1000);
  };

  // Get price per kg (base price is already per kg for sweets)
  const getPricePerKg = () => {
    const basePrice = selectedVariant ? selectedVariant.price : product.price;
    return applyPriceMarkup(basePrice);
  };

  // Calculate total price based on weight (only for sweets)
  const calculateTotalPrice = () => {
    if (product.category === 'Sweets' || product.allowWeightSelection) {
      const pricePerKg = getPricePerKg();
      const totalWeightKg = getTotalWeightInKg();
      return pricePerKg * totalWeightKg;
    }
    // For non-sweets, return the base price
    return selectedVariant ? selectedVariant.price : product.price;
  };

  const handleAddToCart = () => {
    let finalPrice;
    let displayName;
    
    // Check if this is a weight-based product (sweets)
    if (product.category === 'Sweets' || product.allowWeightSelection) {
      const totalWeightKg = getTotalWeightInKg();
      finalPrice = calculateTotalPrice();
      
      // Format weight display
      const kg = parseFloat(weightKg) || 0;
      const gram = parseFloat(weightGram) || 0;
      let weightDisplay = '';
      if (kg > 0 && gram > 0) {
        weightDisplay = `${kg}kg ${gram}g`;
      } else if (kg > 0) {
        weightDisplay = `${kg}kg`;
      } else if (gram > 0) {
        weightDisplay = `${gram}g`;
      }
      
      displayName = selectedVariant 
        ? `${product.name} - ${selectedVariant.name} (${weightDisplay})` 
        : `${product.name} (${weightDisplay})`;
    } else {
      // For non-sweets, use regular pricing
      finalPrice = selectedVariant ? selectedVariant.price : product.price;
      displayName = selectedVariant 
        ? `${product.name} - ${selectedVariant.name}` 
        : product.name;
    }
    
    const productToAdd = {
      ...product,
      variant: selectedVariant,
      price: finalPrice,
      displayName,
      weight: (product.category === 'Sweets' || product.allowWeightSelection) 
        ? { kg: parseFloat(weightKg) || 0, gram: parseFloat(weightGram) || 0, totalKg: getTotalWeightInKg() }
        : null
    };
    
    for (let i = 0; i < quantity; i++) {
      addToCart(productToAdd);
    }
    
    alert(`Added ${quantity} ${productToAdd.displayName} to cart!`);
  };

  const currentPrice = calculateTotalPrice();

  const productSchema = generateProductSchema({
    ...product,
    price: currentPrice
  });

  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: 'Home', url: 'https://mdsweets.shop/' },
    { name: 'Shop', url: 'https://mdsweets.shop/shop' },
    { name: product.name, url: `https://mdsweets.shop/product/${product.id}` }
  ]);

  const combinedSchema = {
    "@context": "https://schema.org",
    "@graph": [productSchema, breadcrumbSchema]
  };

  return (
    <div className="product-detail">
      <SEOHelmet 
        title={`${product.name} - Buy Online | MD Sweets Caterers`}
        description={product.longDescription || product.description}
        keywords={`${product.name.toLowerCase()}, buy ${product.name.toLowerCase()} online, ${product.name.toLowerCase()} price, order ${product.name.toLowerCase()}`}
        image={product.image}
        type="product"
        structuredData={combinedSchema}
      />
      <div className="product-detail-container">
        <button onClick={() => navigate('/shop')} className="btn-back">
          ← Back to Shop
        </button>
        
        <div className="product-detail-content">
          <div className="product-image-section">
            <img 
              src={selectedVariant?.image || product.image} 
              alt={product.name} 
              className="product-main-image"
            />
            {product.gallery && (
              <div className="product-gallery">
                {product.gallery.map((img, index) => (
                  <img key={index} src={img} alt={`${product.name} ${index + 1}`} />
                ))}
              </div>
            )}
          </div>

          <div className="product-info-section">
            <h1>{product.name}</h1>
            <p className="product-description-full">{product.description}</p>
            
            {product.longDescription && (
              <div className="product-long-description">
                <h3>About this product</h3>
                <p>{product.longDescription}</p>
              </div>
            )}

            <div className="product-price-section">
              <div className="price-info">
                <span className="price-label">
                  {(product.category === 'Sweets' || product.allowWeightSelection) ? 'Price per kg:' : 'Price:'}
                </span>
                <span className="current-price">₹{getPricePerKg()}</span>
                {selectedVariant && selectedVariant.originalPrice && (
                  <span className="original-price">₹{selectedVariant.originalPrice}</span>
                )}
              </div>
            </div>

            {product.variants && product.variants.length > 0 && (
              <div className="product-variants">
                <h3>Choose Variant {(product.category === 'Sweets' || product.allowWeightSelection) && '(Price per kg)'}</h3>
                <div className="variants-grid">
                  {product.variants.map((variant, index) => (
                    <div 
                      key={index}
                      className={`variant-option ${selectedVariant?.name === variant.name ? 'selected' : ''}`}
                      onClick={() => setSelectedVariant(variant)}
                    >
                      <div className="variant-name">{variant.name}</div>
                      <div className="variant-price">
                        ₹{variant.price}{(product.category === 'Sweets' || product.allowWeightSelection) && '/kg'}
                      </div>
                      {variant.description && (
                        <div className="variant-description">{variant.description}</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Weight Input Section - Only for Sweets */}
            {(product.category === 'Sweets' || product.allowWeightSelection) && (
              <div className="custom-weight-section">
                <h3>Select Weight</h3>
                <div className="weight-inputs-row">
                  <div className="weight-input-wrapper">
                    <label>Kilograms</label>
                    <input 
                      type="number" 
                      min="0"
                      step="1"
                      value={weightKg}
                      onChange={(e) => setWeightKg(e.target.value)}
                      placeholder="0"
                      className="weight-input"
                    />
                    <span className="unit-label">kg</span>
                  </div>
                  <div className="weight-plus">+</div>
                  <div className="weight-input-wrapper">
                    <label>Grams</label>
                    <input 
                      type="number" 
                      min="0"
                      max="999"
                      step="50"
                      value={weightGram}
                      onChange={(e) => setWeightGram(e.target.value)}
                      placeholder="0"
                      className="weight-input"
                    />
                    <span className="unit-label">g</span>
                  </div>
                </div>
                
                {/* Quick Weight Presets */}
                <div className="weight-presets">
                  <button 
                    className="preset-btn" 
                    onClick={() => { setWeightKg('0'); setWeightGram('250'); }}
                  >
                    250g
                  </button>
                  <button 
                    className="preset-btn" 
                    onClick={() => { setWeightKg('0'); setWeightGram('500'); }}
                  >
                    500g
                  </button>
                  <button 
                    className="preset-btn" 
                    onClick={() => { setWeightKg('1'); setWeightGram('0'); }}
                  >
                    1kg
                  </button>
                  <button 
                    className="preset-btn" 
                    onClick={() => { setWeightKg('2'); setWeightGram('0'); }}
                  >
                    2kg
                  </button>
                  <button 
                    className="preset-btn" 
                    onClick={() => { setWeightKg('5'); setWeightGram('0'); }}
                  >
                    5kg
                  </button>
                </div>
                
                <div className="weight-price-info">
                  <p>Price per kg: ₹{getPricePerKg().toFixed(2)}</p>
                  <p>Total weight: {getTotalWeightInKg().toFixed(3)} kg</p>
                  <p className="calculated-price">
                    Total: ₹{calculateTotalPrice().toFixed(2)}
                  </p>
                </div>
              </div>
            )}

            {/* Quantity Section */}
            <div className="quantity-section">
              <label>Quantity:</label>
              <div className="quantity-controls">
                <button 
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="quantity-btn"
                >
                  -
                </button>
                <span className="quantity-display">{quantity}</span>
                <button 
                  onClick={() => setQuantity(quantity + 1)}
                  className="quantity-btn"
                >
                  +
                </button>
              </div>
            </div>

            <button 
              onClick={handleAddToCart} 
              className="btn-add-to-cart"
              disabled={(product.category === 'Sweets' || product.allowWeightSelection) && getTotalWeightInKg() <= 0}
            >
              Add to Cart - ₹{currentPrice ? (currentPrice * quantity).toFixed(2) : '0.00'}
            </button>

            {product.ingredients && (
              <div className="product-ingredients">
                <h3>Ingredients</h3>
                <p>{product.ingredients}</p>
              </div>
            )}

            {product.nutritionInfo && (
              <div className="nutrition-info">
                <h3>Nutrition Information</h3>
                <div className="nutrition-grid">
                  {Object.entries(product.nutritionInfo).map(([key, value]) => (
                    <div key={key} className="nutrition-item">
                      <span className="nutrition-label">{key}:</span>
                      <span className="nutrition-value">{value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {relatedProducts.length > 0 && (
          <div className="related-products">
            <h2>You might also like</h2>
            <div className="related-products-grid">
              {relatedProducts.map(relatedProduct => (
                <ProductCard key={relatedProduct.id} product={relatedProduct} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductDetail;